/**
 * The step-by-step purchase flow: Add to Cart opens an accessories page for the
 * device, and the accessories, cart and checkout pages share a progress bar.
 * On for every customer in every store (phase 2). The signature is kept so the
 * call sites, and a rollback, stay one-line changes.
 */
export const isPurchaseFlowEnabled = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _email?: string | null,
): boolean => true;

/**
 * Next.js route of the accessories page. It brings its own header and bottom
 * bar, so it has no site navbar and only the compact footer.
 */
export const ACCESSORIES_ROUTE = '/accessories/[skuId]';

/**
 * Accessories page for a device, keyed by the SKU that was added to the cart.
 * The page reads that SKU from MsCatalog2, which also gives it the product's
 * PSKU for the recommendations.
 */
export const getAccessoriesPath = (skuId: string): string =>
  `/accessories/${encodeURIComponent(skuId)}`;

export const LAST_ADDED_COOKIE = 'reebelo_purchase_flow_last_added';

const LAST_ADDED_MAX_AGE = 60 * 60 * 24 * 30; // 30 days, about a cart's life

/** The device last added from its product page, as the flow remembers it. */
export type LastAddedT = {
  skuId: string;
  /**
   * Its accessories page had nothing to show and went straight on to the
   * cart, so the progress bar leaves the Accessories step out.
   */
  accessoriesSkipped: boolean;
};

/** `Set-Cookie` / `document.cookie` value storing the last added device. */
export const serializeLastAddedCookie = (value: LastAddedT): string =>
  `${LAST_ADDED_COOKIE}=${encodeURIComponent(
    JSON.stringify(value),
  )}; Path=/; Max-Age=${LAST_ADDED_MAX_AGE}; SameSite=Lax`;

/** Reads the cookie's (still URI-encoded) value; null when unreadable. */
export const parseLastAddedCookie = (
  value: string | null | undefined,
): LastAddedT | null => {
  try {
    const parsed = value ? JSON.parse(decodeURIComponent(value)) : null;

    return typeof parsed?.skuId === 'string'
      ? {
          skuId: parsed.skuId,
          accessoriesSkipped: parsed.accessoriesSkipped === true,
        }
      : null;
  } catch {
    return null;
  }
};

/**
 * Remembers the device last added to the cart from its product page, so the
 * progress bar's Accessories step on the cart and checkout returns to that
 * device rather than to whichever line happens to come last in the cart.
 * Per-browser convenience only: losing it just means the fallback is used.
 */
export const rememberLastAddedSku = (skuId: string): void => {
  document.cookie = serializeLastAddedCookie({
    skuId,
    accessoriesSkipped: false,
  });
};

/** The last added device as the browser has it (client side only). */
export const getLastAdded = (): LastAddedT | null => {
  const entry = document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith(`${LAST_ADDED_COOKIE}=`));

  return parseLastAddedCookie(entry?.slice(LAST_ADDED_COOKIE.length + 1));
};
